/**
 * useUsage — polls /api/v2/usage/me to expose live cost-cap state.
 *
 * Returns `{ usage, refresh, isLoading, error }`.
 *
 * Default poll cadence: 60 s. Pass `{ pollMs: 0 }` to disable polling
 * (use refresh() manually). When the user is admin or the cap is
 * disabled, the hook keeps polling but the consumer can ignore the
 * banner via `usage.cap_enabled === false`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const BACKEND_URL = getBackendBaseUrl();
const URL = `${BACKEND_URL}/api/v2/usage/me`;

export const useUsage = ({ pollMs = 60_000 } = {}) => {
  const { session } = useAuth();
  const [usage, setUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const headers = {};
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      const r = await fetch(URL, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setUsage(data);
      return data;
    } catch (e) {
      setError(e.message || String(e));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    refresh();
    if (pollMs > 0) {
      timerRef.current = setInterval(refresh, pollMs);
      return () => clearInterval(timerRef.current);
    }
  }, [refresh, pollMs]);

  return { usage, refresh, isLoading, error };
};

export default useUsage;
